<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Icon from '../Icon.svelte';
	import {
		buildFilterValue,
		FilterCondition,
		FilterType,
		saveListSettingsToLocalStorage,
		type SimpleListListFilter
	} from '../../types/ListTypes';

	export const doneTypingInterval = 500;
	interface Props {
		//Should the input automatically be focused?
		autoFocus?: boolean;
		placeholder?: any;
		filter: SimpleListListFilter;
		onSearchTrigger: () => void;
		onBlur?: () => void;
	}

	let {
		autoFocus = false,
		placeholder = $_('general.lbl_search_hint'),
		filter = $bindable(),
		onSearchTrigger,
		onBlur = undefined
	}: Props = $props();

	let typingTimer: NodeJS.Timeout; //timer identifier

	let emptyOrTrailingSpace: boolean = $state(false);

	function handleBeforeInput(event: InputEvent) {
		const target = event.target as HTMLTextAreaElement;
		//Build the next value
		const nextVal =
			target.value.substring(0, target.selectionStart) +
			(event.data ?? '') +
			target.value.substring(target.selectionEnd);

		// Block the "|" character
		if (nextVal.includes('|')) {
			event.preventDefault();
			return;
		}
	}

	function checkForEmpyOrTailingSpace() {
		let filterInput = filter.searchBarInput;

		if (filterInput != undefined && filterInput.length > 0) {
			//Get the last character
			let lastChar = filterInput[filterInput.length - 1];

			//Set the empty info
			if (lastChar == ' ' || filterInput.trim().length === 0) {
				emptyOrTrailingSpace = true;
			} else {
				emptyOrTrailingSpace = false;
			}
		} else {
			emptyOrTrailingSpace = false;
		}
	}

	function handleSearchTrigger() {
		checkForEmpyOrTailingSpace();

		//Set the filter values
		if ((filter.searchBarInput?.length || 0) <= 0) {
			resetFilterValues();
		} else {
			setFilterValues();
		}

		//Save the filters to memory.
		saveListSettingsToLocalStorage(filter);

		onSearchTrigger();
	}

	/**
	 * Set all filtervalues except for the write protected.
	 */
	function setFilterValues() {
		filter.filterOptions.forEach((option) => {
			let writeProtected = option.resetProtect != null ? option.resetProtect : false;
			let isOrSearch = option.orSearch != null ? option.orSearch : true;

			//Only reset the value if set
			if (filter.searchBarInput && !writeProtected) {
				//Set the value to or search fields
				if (isOrSearch) {
					if (option.type == 'tags') {
						option.value = buildFilterValue(
							filter.searchBarInput,
							FilterCondition.CO,
							FilterType.OR
						);
					} else {
						option.value = buildFilterValue(
							filter.searchBarInput,
							FilterCondition.EQ,
							FilterType.OR
						);
					}
				} else {
					//Set the default value if we have or else null
					option.value = option.defaultValue ? option.defaultValue : null;
				}
			}
		});
	}

	/**
	 * Reset all none write protected values back to null.
	 */
	function resetFilterValues() {
		//Remove all the values which are not reset protected
		filter.filterOptions.forEach((filter) => {
			let writeProtected = filter.resetProtect != null ? filter.resetProtect : false;
			//Clear the value
			if (!writeProtected) {
				//Set the default value if we have or else null
				filter.value = filter.defaultValue ? filter.defaultValue : null;
			}
		});
	}

	function handleKeyup() {
		clearTimeout(typingTimer);
		typingTimer = setTimeout(() => {
			handleSearchTrigger();
		}, doneTypingInterval);
	}

	function init(element: HTMLInputElement) {
		if (autoFocus) {
			element.focus();
		}

		checkForEmpyOrTailingSpace();
	}
</script>

<div
	class="flex px-2 items-center outline-none bg-white dark:text-white dark:bg-dark-200 h-9 rounded-full border-2 border-gray dark:border-transparent"
>
	{#if (filter.searchBarInput?.length || 0) > 0}
		<button
			onclick={() => {
				filter.searchBarInput = '';
				handleKeyup();
			}}
		>
			<Icon name="ban" classInfo="dark:fill-secondary-500" />
		</button>
	{:else}
		<Icon name="search" classInfo="dark:fill-white" />
	{/if}
	<input
		use:init
		{placeholder}
		bind:value={filter.searchBarInput}
		onkeyup={handleKeyup}
		onblur={() => onBlur?.()}
		onbeforeinput={handleBeforeInput}
		class="py-2 pl-2 pr-1 w-full outline-none bg-transparent border-0 dark:text-white"
	/>

	{#if emptyOrTrailingSpace}
		<Icon
			name="info"
			classInfo="fill-secondary-200 dark:fill-orange"
			hoverText={$_('general.lbl_trailing_space')}
		/>
	{:else}
		<div class="w-[20px]"></div>
	{/if}
</div>
